/**
 * CustomerPicker — header "View as Customer A vs B" dropdown (2026-06-29).
 *
 * Renders in the Hub admin header. Pattern:
 *   [ Industry: Oil & Gas ▼ ]   [ Viewing as: ALL CUSTOMERS ▼ ]
 *                                         ↓ (Hub admin opens)
 *                                  Acme Offshore
 *                                  TankFarmCorp LLC
 *                                  ──────────
 *                                  ✓ All customers (industry-wide)
 */

import { useCustomer } from '../../contexts/CustomerContext'
import { useIndustry } from '../../contexts/IndustryContext'
import { Users, ChevronDown } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'

export default function CustomerPicker() {
  const { organizationId, customerName, customers, canSwitchCustomer, setCustomer, loading } = useCustomer()
  const { industryId } = useIndustry()
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  if (!canSwitchCustomer) {
    // Spoke users see a static label of their own org (no dropdown)
    return customerName ? (
      <div className="flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-surface-800/60 border border-surface-700/50 text-surface-300">
        <Users size={12} className="text-surface-500" />
        <span>{customerName}</span>
      </div>
    ) : null
  }

  const label = organizationId
    ? (customerName || 'Selected customer')
    : 'All customers'

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-surface-800/80 border border-surface-700/60 hover:bg-surface-700/60 text-surface-200 transition-colors min-w-[180px] justify-between"
        title={`Switch view across customers in ${industryId || 'current industry'}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex items-center gap-1.5">
          <Users size={12} className={organizationId ? 'text-blue-300' : 'text-surface-500'} />
          <span className="text-surface-400">View as:</span>
          <span className={organizationId ? 'text-blue-200 font-semibold' : 'text-surface-200'}>{label}</span>
        </span>
        <ChevronDown size={12} className={`text-surface-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-72 bg-surface-900 border border-surface-700 rounded shadow-xl z-50 max-h-96 overflow-y-auto">
          <div className="px-3 py-2 border-b border-surface-800 text-[10px] uppercase tracking-wider text-surface-500">
            {industryId ? industryId.replace(/_/g, ' ') : 'all industries'} · {customers.length} customer{customers.length === 1 ? '' : 's'}
          </div>

          {loading && (
            <div className="px-3 py-2 text-xs text-surface-500">Loading customers…</div>
          )}

          {!loading && customers.map(c => {
            const active = c.id === organizationId
            return (
              <button
                key={c.id}
                onClick={() => { setCustomer(c.id); setOpen(false) }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-surface-800/60 ${active ? 'bg-blue-500/10' : ''}`}
                role="option"
                aria-selected={active}
              >
                <div className={`font-semibold ${active ? 'text-blue-200' : 'text-surface-100'}`}>
                  {active && '✓ '}{c.name}
                </div>
                <div className="text-[10px] text-surface-500 font-mono">
                  {c.slug} · {c.industry || '—'}
                </div>
              </button>
            )
          })}

          {!loading && customers.length === 0 && (
            <div className="px-3 py-3 text-xs text-surface-500 text-center italic">
              No customers in this industry yet
            </div>
          )}

          <div className="border-t border-surface-800">
            <button
              onClick={() => { setCustomer(null); setOpen(false) }}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-surface-800/60 ${!organizationId ? 'bg-blue-500/10' : ''}`}
              role="option"
              aria-selected={!organizationId}
            >
              <div className={`font-semibold ${!organizationId ? 'text-blue-200' : 'text-surface-100'}`}>
                {!organizationId && '✓ '}All customers (industry-wide)
              </div>
              <div className="text-[10px] text-surface-500">
                Show only industry-shared cards / no customer override
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
